function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import React, { useEffect, useMemo, useState } from 'react';
import { TextField, Typography, KeyboardDatePicker, MenuItem, useStateInRef } from 'seagull';
import { richTextValidation } from 'validators';
import { Controller } from 'react-hook-form';
import { isAfter, isBefore, isSameDay } from 'date-fns';
import { useIoContext } from "../../contexts";
import { BillMode, FormKey, ctaTextMap, getBillModeOptions, getDateRange, getDefaultBillMode, getDefaultIssueDate, getMaxDateMessage, getMinDateMessage, normalizeEffectiveDate, selectorWidth } from "./ManualBillModal.utils";
import { ManualBillModalLayout } from "./ManualBillModalLayout";
import styles from "./ManualBillModal.module.scss";
import { connectControllerToKeyboardDatePicker } from "../../utils/react-hook-form.utils";
export default function ManualBillModal(props) {
  const {
    title,
    manualServiceBilling,
    disableFields,
    form,
    invoiceDateInfo,
    effectiveDate = null,
    onCancel,
    onSubmit,
    infoBoxContent,
    tableContent,
    agreementPickerContent
  } = props;
  const getProps = useStateInRef(props);
  const {
    getNow
  } = useIoContext();
  const billModeOptions = useMemo(() => {
    return getBillModeOptions(getNow(), effectiveDate, invoiceDateInfo);
  }, [effectiveDate, invoiceDateInfo]);
  const [mode, setMode] = useState(() => getDefaultBillMode(billModeOptions, invoiceDateInfo));
  const {
    control,
    watch
  } = form;
  const issueDate = watch(FormKey.IssueDate);
  const note = watch(FormKey.Note);
  const manualBillData = {
    mode,
    issueDate,
    note,
    changeMode: setMode
  };
  const {
    minDate,
    maxDate
  } = useMemo(() => {
    return getDateRange(mode, manualServiceBilling, getNow(), normalizeEffectiveDate(effectiveDate));
  }, [mode, manualServiceBilling, effectiveDate]);
  const validateIssueDate = {
    validate(value) {
      const isInvalid = !value || !!minDate && isBefore(value, minDate) || !!maxDate && isAfter(value, maxDate);
      return !isInvalid || 'Invalid date';
    }
  };
  useEffect(() => {
    setMode(getDefaultBillMode(billModeOptions, invoiceDateInfo));
  }, [billModeOptions, invoiceDateInfo]);
  useEffect(() => {
    const {
      form
    } = getProps();
    const newIssueDate = getDefaultIssueDate(mode, invoiceDateInfo, getNow(), normalizeEffectiveDate(effectiveDate));
    form.setValue(FormKey.IssueDate, newIssueDate, {
      shouldValidate: true
    });
  }, [mode, invoiceDateInfo, effectiveDate]);
  return /*#__PURE__*/React.createElement(ManualBillModalLayout, {
    title: title,
    primaryButtonLabel: ctaTextMap[mode],
    "data-testid": "manual-bill",
    onPrimaryButtonClick: () => onSubmit(manualBillData),
    onCloseButtonClick: onCancel,
    topSlot: /*#__PURE__*/React.createElement(React.Fragment, null, agreementPickerContent && agreementPickerContent(manualBillData), /*#__PURE__*/React.createElement("div", {
      className: styles.modeRow
    }, /*#__PURE__*/React.createElement(TextField, {
      select: true,
      width: selectorWidth,
      value: mode,
      label: "Billing date",
      testid: "manual-bill-mode",
      disabled: disableFields
    }, billModeOptions.map(_ref => {
      let {
        value,
        text,
        disabled,
        tooltip
      } = _ref;
      return /*#__PURE__*/React.createElement(MenuItem, {
        key: value,
        value: value,
        disabled: disabled,
        tooltipProps: tooltip ? {
          placement: 'top',
          title: tooltip
        } : undefined,
        "data-testid": "mode-".concat(value),
        onClick: () => setMode(value)
      }, text);
    })), mode === BillMode.FutureDate && /*#__PURE__*/React.createElement(Controller, {
      control: control,
      name: FormKey.IssueDate,
      rules: validateIssueDate,
      render: props => /*#__PURE__*/React.createElement(KeyboardDatePicker, _extends({
        "data-testid": "manual-bill-future-date",
        style: {
          width: selectorWidth
        },
        minDate: minDate,
        maxDate: maxDate,
        minDateMessage: getMinDateMessage(minDate),
        maxDateMessage: getMaxDateMessage(maxDate)
      }, connectControllerToKeyboardDatePicker(props), {
        highlightDay: invoiceDateInfo ? day => invoiceDateInfo.futureInvoicesDates.some(d => isSameDay(day, d)) : undefined
      }))
    }))),
    infoSlot: infoBoxContent(manualBillData),
    issueDateRow: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Typography, {
      weight: "normal"
    }, "Issue date"), /*#__PURE__*/React.createElement(Controller, {
      control: control,
      name: FormKey.IssueDate,
      rules: validateIssueDate,
      render: props => /*#__PURE__*/React.createElement(KeyboardDatePicker, _extends({
        size: "small",
        className: styles.datepicker,
        "data-testid": "manual-bill-issue-date",
        disabled: !(mode === BillMode.Now && manualServiceBilling),
        minDate: minDate,
        maxDate: maxDate,
        minDateMessage: getMinDateMessage(minDate),
        maxDateMessage: getMaxDateMessage(maxDate)
      }, connectControllerToKeyboardDatePicker(props)))
    })),
    tableSlot: tableContent(manualBillData),
    notesSlot: /*#__PURE__*/React.createElement(Controller, {
      control: control,
      name: FormKey.Note,
      rules: richTextValidation('Note'),
      render: _ref2 => {
        let {
          field: {
            value,
            ref,
            onChange,
            onBlur
          },
          fieldState: {
            isTouched,
            error
          }
        } = _ref2;
        return /*#__PURE__*/React.createElement(TextField, {
          multiline: true,
          label: "Notes (optional)",
          minRows: 3,
          maxRows: 3,
          testid: "manual-bill-note",
          disabled: disableFields,
          error: isTouched && !!error,
          helperText: isTouched && error && error.message,
          value: value,
          inputRef: ref,
          onChange: onChange,
          onBlur: onBlur
        });
      }
    })
  });
}